import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Navigation } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Stars } from "@/components/Brand";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const s = settings || {};
  const [review, setReview] = useState({ name: "", rating: 5, comment: "" });
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#review") {
      const el = document.getElementById("review");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [location.hash]);

  const submit = async (e) => {
    e.preventDefault();
    if (!review.comment) return toast.error("Please write your review");
    setBusy(true);
    try { const { data } = await api.post("/reviews", review); toast.success(data.message); setReview({ name: "", rating: 5, comment: "" }); }
    catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  return (
    <div>
      <div className="bg-metros-black text-white grain px-4 md:px-6 py-10">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight">Contact & Location</h1>
          <p className="text-gray-400 mt-1">We'd love to hear from you.</p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-3">
            <p className="flex items-start gap-3"><MapPin className="text-metros-red mt-0.5" size={20} /> <span>{s.name}<br /><span className="text-muted-foreground text-sm">{s.address}</span></span></p>
            <p className="flex items-center gap-3"><Phone className="text-metros-red" size={20} /> {s.phone}</p>
            <p className="flex items-center gap-3"><Mail className="text-metros-red" size={20} /> {s.email}</p>
            <div className="flex gap-2 pt-2">
              <a href={`tel:${s.phone}`} data-testid="call-now" className="flex-1 text-center bg-metros-red text-white rounded-full py-3 font-bold flex items-center justify-center gap-2"><Phone size={16} /> Call Now</a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(s.address || "")}`} target="_blank" rel="noreferrer" data-testid="directions" className="flex-1 text-center bg-metros-black text-white rounded-full py-3 font-bold flex items-center justify-center gap-2"><Navigation size={16} /> Directions</a>
            </div>
          </div>
          {(s.address || s.name) && (
            <a data-testid="contact-map" target="_blank" rel="noopener noreferrer"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.name)}`}
              className="block rounded-2xl overflow-hidden border border-gray-100 group relative">
              <iframe title="METROS location" width="100%" height="220" style={{ border: 0, pointerEvents: "none" }} loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(s.address || s.name)}&z=15&output=embed`} />
              <span className="absolute bottom-2 right-2 bg-white/95 text-metros-black text-xs font-semibold rounded-full px-3 py-1 shadow flex items-center gap-1 group-hover:bg-metros-red group-hover:text-white transition-colors"><Navigation size={12} /> Open map</span>
            </a>
          )}
          <div className="bg-white border border-gray-100 rounded-2xl p-6">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><Clock size={18} className="text-metros-red" /> Opening Hours</h3>
            <div className="space-y-1.5 text-sm">
              {days.map((d) => {
                const h = s.opening_hours?.[d];
                return <div key={d} className="flex justify-between"><span className="capitalize text-muted-foreground">{d}</span><span>{h?.closed ? "Closed" : `${h?.open} – ${h?.close}`}</span></div>;
              })}
            </div>
          </div>
        </div>
        <div id="review" className="bg-white border border-gray-100 rounded-2xl p-6 scroll-mt-24">
          <h3 className="font-heading font-bold text-xl mb-1">Leave a Review</h3>
          <p className="text-sm text-muted-foreground mb-4">Share your METROS experience.</p>
          <form onSubmit={submit} className="space-y-3">
            <input data-testid="review-name" placeholder="Your name" value={review.name} onChange={(e) => setReview({ ...review, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Rating:</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} data-testid={`star-${n}`} onClick={() => setReview({ ...review, rating: n })} className={`text-2xl ${n <= review.rating ? "text-metros-red" : "text-gray-300"}`}>★</button>
              ))}
            </div>
            <textarea data-testid="review-comment" placeholder="Your review…" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm h-28 resize-none focus:ring-2 focus:ring-metros-red focus:outline-none" />
            <button data-testid="submit-review" disabled={busy} className="w-full bg-metros-red text-white rounded-full py-3 font-bold disabled:opacity-60">Submit Review</button>
          </form>
        </div>
      </div>
    </div>
  );
}
